import { Controller } from "@hotwired/stimulus"

// A pill multiselect built on top of a real <select multiple>.
//
//   <div class="field" data-controller="multiselect">
//     <label for="skill_agents">Written for</label>
//     <select id="skill_agents" name="skill[agents][]" multiple
//             data-multiselect-target="source">…</select>
//   </div>
//
// The select stays the form control and the only source of truth. This
// controller hides it and draws a combobox plus a pill for each selected
// option; every interaction ends by flipping `option.selected` and re-rendering
// from the select. Nothing is mirrored into hidden inputs, so there is no second
// copy of the state to disagree with the first.
//
// That also means it degrades to something usable rather than to nothing: with
// no JavaScript the native multi-select is still there and still submits, which
// is exactly what the form did before this existed.
//
// Keyboard: ↓/↑ move, Enter or Space toggles, Escape closes, Backspace on an
// empty input removes the last pill, Home/End jump. Typing filters.
export default class extends Controller {
  static targets = ["source"]
  static values = {
    placeholder: { type: String, default: "Type to search…" }
  }

  connect() {
    // Idempotent on purpose. Turbo caches the DOM *including* whatever we drew
    // last time, so connect() can run against a page that already has a shell in
    // it. Clearing first means a restored page cannot end up with two.
    this.teardown()

    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.activeIndex = -1
    this.query = ""

    this.build()
    this.render()
  }

  disconnect() {
    this.teardown()
    document.removeEventListener("click", this.onDocumentClick)
  }

  // --- Construction --------------------------------------------------------

  build() {
    const source = this.sourceTarget
    const baseId = source.id || `multiselect-${Math.random().toString(36).slice(2)}`

    this.listId = `${baseId}-listbox`

    // Hidden, not disabled: a disabled control is left out of the submission,
    // which would silently drop every selection.
    source.hidden = true
    // It must also leave the tab order, or keyboard users hit an invisible
    // listbox on the way to the visible one.
    source.tabIndex = -1
    source.setAttribute("aria-hidden", "true")

    this.shell = document.createElement("div")
    this.shell.className = "multiselect"
    this.shell.dataset.multiselectGenerated = "true"

    this.field = document.createElement("div")
    this.field.className = "multiselect__field"
    this.field.addEventListener("mousedown", (event) => {
      // Clicking the padding focuses the input, the way a real text field
      // behaves. Guarded so it does not steal a click aimed at a remove button.
      if (event.target === this.field || event.target === this.pillList) {
        event.preventDefault()
        this.input.focus()
        this.open()
      }
    })

    this.pillList = document.createElement("ul")
    this.pillList.className = "multiselect__pills"

    this.input = document.createElement("input")
    this.input.type = "text"
    this.input.className = "multiselect__input"
    this.input.autocomplete = "off"
    this.input.setAttribute("role", "combobox")
    this.input.setAttribute("aria-expanded", "false")
    this.input.setAttribute("aria-controls", this.listId)
    this.input.setAttribute("aria-autocomplete", "list")
    this.input.id = `${baseId}-input`

    this.input.addEventListener("input", () => {
      this.query = this.input.value.trim().toLowerCase()
      this.activeIndex = -1
      this.open()
      this.renderMenu()
    })
    this.input.addEventListener("keydown", (event) => this.onKeydown(event))
    this.input.addEventListener("focus", () => this.open())

    this.menu = document.createElement("ul")
    this.menu.className = "multiselect__menu"
    this.menu.id = this.listId
    this.menu.setAttribute("role", "listbox")
    this.menu.setAttribute("aria-multiselectable", "true")
    this.menu.hidden = true
    // Pointer, not click: mousedown on an option would blur the input first and
    // close the menu before the click ever lands.
    this.menu.addEventListener("mousedown", (event) => event.preventDefault())
    this.menu.addEventListener("click", (event) => {
      const option = event.target.closest("[data-value]")
      if (!option) return

      // A click we handled is never an outside click. Without this the menu shuts
      // on every pick: re-rendering removes the <li> that was clicked, so by the
      // time the event reaches the document listener its target is detached and
      // `element.contains(target)` is false. Picking two things in a row — the
      // entire point of a multiselect — would be impossible.
      event.stopPropagation()

      this.toggleValue(option.dataset.value)
      this.input.focus()
    })

    // Selection changes are announced rather than left for a screen reader to
    // discover: the pill appears somewhere the user is not currently reading.
    this.status = document.createElement("p")
    this.status.className = "visually-hidden"
    this.status.setAttribute("role", "status")
    this.status.setAttribute("aria-live", "polite")

    this.field.append(this.pillList, this.input)
    this.shell.append(this.field, this.menu, this.status)
    source.after(this.shell)

    // The visible label points at the select we just hid, so it would no longer
    // name the control anyone can see or reach. Repointing it keeps one visible
    // label properly associated, which aria-label alone would not.
    this.label = source.id
      ? this.element.querySelector(`label[for="${source.id}"]`)
      : null
    if (this.label) this.label.setAttribute("for", this.input.id)

    document.addEventListener("click", this.onDocumentClick)
  }

  teardown() {
    this.element.querySelectorAll("[data-multiselect-generated]").forEach((node) => node.remove())

    if (this.label && this.sourceTarget.id) {
      this.label.setAttribute("for", this.sourceTarget.id)
    }

    if (this.hasSourceTarget) {
      this.sourceTarget.hidden = false
      this.sourceTarget.removeAttribute("aria-hidden")
      this.sourceTarget.removeAttribute("tabindex")
    }
  }

  // --- Rendering -----------------------------------------------------------

  render() {
    this.renderPills()
    this.renderMenu()
  }

  renderPills() {
    this.pillList.replaceChildren()

    const selected = this.selectedOptions

    // The placeholder carries the empty state on its own. An extra "nothing
    // selected yet" node beside it put two greys in the box competing to be read,
    // and the invitation is the more useful of the two. Once there are pills the
    // placeholder goes: they answer the question it was asking.
    this.input.placeholder = selected.length === 0 ? this.placeholderValue : ""

    selected.forEach((option) => {
      const pill = document.createElement("li")
      pill.className = "pill"

      const label = document.createElement("span")
      label.className = "pill__label"
      label.textContent = option.text

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "pill__remove"
      // The × is decorative; the accessible name carries which pill this is, so
      // a screen reader does not read a column of identical "remove" buttons.
      remove.innerHTML = "<span aria-hidden=\"true\">&times;</span>"
      remove.setAttribute("aria-label", `Remove ${option.text}`)
      remove.addEventListener("click", (event) => {
        // Same reason as the menu: this button is about to be removed by the
        // re-render, so the outside-click check must not get a look at it.
        event.stopPropagation()
        this.deselect(option.value)
      })

      pill.append(label, remove)
      this.pillList.append(pill)
    })
  }

  renderMenu() {
    this.menu.replaceChildren()

    const matches = this.matchingOptions

    if (matches.length === 0) {
      const empty = document.createElement("li")
      empty.className = "multiselect__no-matches"
      // Not role=option: it is a message, and announcing it as a choice would
      // invite someone to try to pick it.
      empty.textContent = this.query ? `No match for “${this.input.value.trim()}”` : "No options"
      this.menu.append(empty)
      this.input.removeAttribute("aria-activedescendant")
      return
    }

    matches.forEach((option, index) => {
      const item = document.createElement("li")
      item.className = "multiselect__option"
      item.id = `${this.listId}-${index}`
      item.dataset.value = option.value
      item.setAttribute("role", "option")
      item.setAttribute("aria-selected", String(option.selected))
      if (index === this.activeIndex) item.dataset.active = "true"

      const tick = document.createElement("span")
      tick.className = "multiselect__tick"
      tick.setAttribute("aria-hidden", "true")
      // Selected options stay in the list rather than disappearing from it, so
      // the list does not reshuffle under the cursor and so a value can be
      // switched off from the same place it was switched on.
      tick.textContent = option.selected ? "✕" : "+"

      const label = document.createElement("span")
      label.textContent = option.text

      item.append(tick, label)
      this.menu.append(item)
    })

    const active = this.menu.querySelector("[data-active]")
    if (active) {
      this.input.setAttribute("aria-activedescendant", active.id)
      active.scrollIntoView({ block: "nearest" })
    } else {
      this.input.removeAttribute("aria-activedescendant")
    }
  }

  // --- Behaviour -----------------------------------------------------------

  onKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.open()
        this.moveActive(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.open()
        this.moveActive(-1)
        break
      case "Home":
        if (!this.isOpen) return
        event.preventDefault()
        this.setActive(0)
        break
      case "End":
        if (!this.isOpen) return
        event.preventDefault()
        this.setActive(this.matchingOptions.length - 1)
        break
      case "Enter": {
        // Only swallow Enter when it is actually choosing something. Otherwise
        // it belongs to the form, and a combobox that eats Enter is a combobox
        // people cannot submit from.
        const active = this.matchingOptions[this.activeIndex]
        if (!this.isOpen || !active) return
        event.preventDefault()
        this.toggleValue(active.value)
        break
      }
      case " ": {
        // Space only picks when there is nothing typed — otherwise it is part of
        // a search term like "code review".
        const active = this.matchingOptions[this.activeIndex]
        if (!this.isOpen || !active || this.input.value !== "") return
        event.preventDefault()
        this.toggleValue(active.value)
        break
      }
      case "Escape":
        if (!this.isOpen) return
        event.preventDefault()
        this.close()
        break
      case "Backspace": {
        // The expected shortcut, and it only fires on an empty input so it can
        // never eat a character someone is mid-way through deleting.
        if (this.input.value !== "") return
        const last = this.selectedOptions[this.selectedOptions.length - 1]
        if (!last) return
        event.preventDefault()
        this.deselect(last.value)
        break
      }
      case "Tab":
        this.close()
        break
    }
  }

  toggleValue(value) {
    const option = this.optionFor(value)
    if (!option) return

    option.selected = !option.selected
    this.announce(`${option.text} ${option.selected ? "added" : "removed"}.`)
    this.commit()

    // The search term has done its job once something is picked, and leaving it
    // behind means the next choice starts from a filtered list.
    this.input.value = ""
    this.query = ""
    this.activeIndex = -1
    this.render()
  }

  deselect(value) {
    const option = this.optionFor(value)
    if (!option) return

    option.selected = false
    this.announce(`${option.text} removed.`)
    this.commit()
    this.render()

    // Focus would otherwise land on <body> when the button it was on is
    // removed, dropping the keyboard user out of the control entirely.
    this.input.focus()
  }

  // Anything watching the select — autosubmit, a validation library, another
  // controller — should not be able to tell our writes from a person's.
  commit() {
    this.sourceTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.sourceTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  moveActive(step) {
    const count = this.matchingOptions.length
    if (count === 0) return

    // Wraps, and a first ArrowUp lands on the last option rather than nowhere.
    const next = this.activeIndex === -1 && step === -1 ? count - 1 : this.activeIndex + step
    this.setActive((next + count) % count)
  }

  setActive(index) {
    this.activeIndex = index
    this.renderMenu()
  }

  open() {
    if (this.isOpen) return

    this.menu.hidden = false
    this.input.setAttribute("aria-expanded", "true")
    this.renderMenu()
  }

  close() {
    if (!this.isOpen) return

    this.menu.hidden = true
    this.input.setAttribute("aria-expanded", "false")
    this.input.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }

  onDocumentClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  announce(message) {
    this.status.textContent = message
  }

  // --- Reading the select --------------------------------------------------

  get allOptions() {
    return Array.from(this.sourceTarget.options).filter((option) => option.value !== "")
  }

  // In the select's own order, not the order they were clicked — the pills then
  // match the list, and re-adding something does not move it to the end.
  get selectedOptions() {
    return this.allOptions.filter((option) => option.selected)
  }

  get matchingOptions() {
    if (!this.query) return this.allOptions

    return this.allOptions.filter((option) => option.text.toLowerCase().includes(this.query))
  }

  optionFor(value) {
    return this.allOptions.find((option) => option.value === value)
  }

  get isOpen() {
    return !this.menu.hidden
  }
}
